import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Mail, Loader2, Link2, Unlink, CheckCircle2, Calendar } from "lucide-react";
import { useGmail } from "@/hooks/use-gmail";
import { format } from "date-fns";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { getErrorMessage } from "@/lib/errors";

export function EmailSettings() {
  const { connected, account, isLoading, connect, isConnecting, disconnect, isDisconnecting } = useGmail();
  const { toast } = useToast();
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);

  // Connection completes via an OAuth popup that postMessages back to this
  // window (see useGmail). Awaiting it lets us surface popup-blocked / cancelled
  // / timeout errors that would otherwise be silently swallowed.
  const handleConnect = async () => {
    try {
      const result = await connect(window.location.href);
      toast({
        title: "Gmail Connected",
        description: result?.email
          ? `Connected to ${result.email}`
          : "Your Gmail account has been linked.",
      });
    } catch (error) {
      toast({
        title: "Gmail Connection Failed",
        description: getErrorMessage(error),
        variant: "destructive",
      });
    }
  };

  const handleDisconnect = async () => {
    setShowDisconnectConfirm(false);
    try {
      await disconnect();
      toast({
        title: "Gmail Disconnected",
        description: "Your Gmail account has been removed.",
      });
    } catch (error) {
      toast({
        title: "Disconnect Failed",
        description: getErrorMessage(error),
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5" />
                Gmail Integration
              </CardTitle>
              <CardDescription>
                Connect your Gmail account to send emails directly from claims
              </CardDescription>
            </div>
            <Badge variant={connected ? "default" : "secondary"} className="self-start sm:self-auto">
              {connected ? "Connected" : "Not Connected"}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {connected && account ? (
            <>
              {/* Connected account info */}
              <div className="rounded-lg border p-4 space-y-3">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                    <Mail className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{account.gmailEmail}</p>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <CheckCircle2 className="h-3 w-3 text-green-500 dark:text-green-400" />
                      <span>Active</span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-2 border-t pt-2 text-xs text-muted-foreground sm:grid-cols-2 sm:gap-4">
                  <div className="flex items-center gap-1.5">
                    <Calendar className="h-3 w-3" />
                    <span>Connected {format(new Date(account.connectedAt), "MMM d, yyyy")}</span>
                  </div>
                  {account.lastUsedAt && (
                    <div className="flex items-center gap-1.5">
                      <Mail className="h-3 w-3" />
                      <span>Last used {format(new Date(account.lastUsedAt), "MMM d, yyyy")}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Disconnect button */}
              <div className="flex flex-col gap-3 rounded-lg border p-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm font-medium">Disconnect Gmail</p>
                  <p className="text-xs text-muted-foreground">
                    Revoke access and remove your Gmail connection
                  </p>
                </div>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setShowDisconnectConfirm(true)}
                  disabled={isDisconnecting}
                  className="w-full sm:w-auto"
                >
                  {isDisconnecting ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Unlink className="h-4 w-4 mr-2" />
                  )}
                  Disconnect
                </Button>
              </div>
            </>
          ) : (
            /* Not connected - show connect prompt */
            <div className="rounded-lg border border-dashed p-8 text-center space-y-4">
              <div className="flex justify-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                  <Mail className="h-6 w-6 text-muted-foreground" />
                </div>
              </div>
              <div className="space-y-2">
                <h3 className="text-sm font-medium">No Gmail Account Connected</h3>
                <p className="text-xs text-muted-foreground max-w-sm mx-auto">
                  Link your Gmail account to send emails directly from claims.
                  Emails will appear in your Gmail Sent folder and the claim's activity feed.
                </p>
              </div>
              <Button
                onClick={handleConnect}
                disabled={isConnecting}
                className="w-full sm:w-auto"
              >
                {isConnecting ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Link2 className="h-4 w-4 mr-2" />
                )}
                Connect Gmail
              </Button>
            </div>
          )}

          {/* Permissions info */}
          <div className="rounded-lg bg-muted/50 p-4 space-y-2">
            <h4 className="text-xs font-medium">What we access</h4>
            <ul className="text-xs text-muted-foreground space-y-1">
              <li className="flex items-center gap-2">
                <CheckCircle2 className="h-3 w-3 text-green-500 dark:text-green-400 shrink-0" />
                Send emails on your behalf (appears in your Sent folder)
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="h-3 w-3 text-green-500 dark:text-green-400 shrink-0" />
                Search and import email threads you select into claims
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="h-3 w-3 text-green-500 dark:text-green-400 shrink-0" />
                Read your email address for identification
              </li>
            </ul>
            <p className="text-[10px] text-muted-foreground pt-1">
              We only read threads you explicitly search for or import. You can revoke access at any time.
            </p>
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={showDisconnectConfirm} onOpenChange={setShowDisconnectConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disconnect Gmail</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to disconnect your Gmail account? You'll need to
              reconnect before you can send or import emails from claims again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDisconnecting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDisconnect}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={isDisconnecting}
            >
              {isDisconnecting ? "Disconnecting..." : "Disconnect"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
